import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DotContext from "../contexts/DotContext";
import { SessionThemeWrapper } from "../contexts/SessionThemeContext";
import DynamicTitle from "./DynamicTitle";
import Sidebar from "./Sidebar";
import DotMovement from "./DotMovement";
import { markTourCompleted } from "../utils/tourUtils";
import { trackEvent } from "../utils/posthog";

function PublicDemo() {
  const navigate = useNavigate();
  // Skip Demo / Exit Demo always goes to homepage per USER_FEEDBACK
  const returnPath = "/";

  const [currentStep, setCurrentStep] = useState(0);
  const [showDotMovement, setShowDotMovement] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [experienceTimer, setExperienceTimer] = useState(0);
  const [canContinue, setCanContinue] = useState(false);
  const hasTrackedStart = useRef(false);
  const hasTrackedComplete = useRef(false);

  const {
    bgColor,
    dotColor,
    dotGlow,
    dotBrightness,
    dotSize,
    setDotSize,
    dotSpeed,
    setDotSpeed,
    soundFile,
    mute,
    volume,
  } = useContext(DotContext);

  // Track demo started when user begins (moves past welcome)
  useEffect(() => {
    if (currentStep >= 1 && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      trackEvent('demo_started', {
        source: document.referrer || 'direct',
      });
    }
  }, [currentStep]);

  const tourSteps = useMemo(() => [
    {
      id: 'welcome',
      title: 'Welcome to RealignMind!',
      content: 'In the next 2 minutes, you\'ll actually experience bilateral stimulation—not just read about it. Ready to try it?',
      showOverlay: true,
      action: () => {
        setShowDotMovement(false);
        setSidebarVisible(false);
        setExperienceTimer(0);
        setCanContinue(false);
      }
    },
    {
      id: 'what-to-do',
      title: 'Here\'s What You\'ll Do',
      content: 'A dot will move back and forth across the screen. Your job is simple: follow it with your eyes. That\'s it. This left-right eye movement is called bilateral stimulation—the same technique used in clinical research.',
      showOverlay: true,
      action: () => {
        setShowDotMovement(false);
        setSidebarVisible(false);
      }
    },
    {
      id: 'session-structure',
      title: 'How a Session Works',
      content: 'A typical session has a simple flow:',
      showOverlay: true,
      showStructureList: true,
      action: () => {
        setShowDotMovement(false);
        setSidebarVisible(false);
      }
    },
    {
      id: 'try-it',
      title: null,
      showOverlay: false,
      isExperienceStep: true,
      action: () => {
        setShowDotMovement(true);
        setSidebarVisible(false);
        setExperienceTimer(0);
        setCanContinue(false);
      }
    },
    {
      id: 'reflection',
      title: 'What Did You Notice?',
      content: 'Many people report feeling calmer or more focused after just a few seconds. Some notice their thoughts slow down. Others feel a subtle shift they can\'t quite name. Whatever you experienced—or didn\'t—is completely normal.',
      showOverlay: true,
      action: () => {
        setShowDotMovement(false);
        setSidebarVisible(false);
      }
    },
    {
      id: 'complete',
      title: 'You Just Experienced Bilateral Stimulation',
      content: 'That\'s the core of what RealignMind offers—plus mood tracking, guided sessions, and progress history. Full sessions typically last 5-20 minutes.',
      showOverlay: true,
      isCompleteStep: true,
      action: () => {
        setShowDotMovement(false);
        setSidebarVisible(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  // Track demo completed when user reaches final step
  useEffect(() => {
    const step = tourSteps[currentStep];
    if (step?.id === 'complete' && !hasTrackedComplete.current) {
      hasTrackedComplete.current = true;
      trackEvent('demo_completed', { time_spent_seconds: experienceTimer });
    }
  }, [currentStep, experienceTimer, tourSteps]);

  // Timer for the "try it" step - short minimum before continue so users aren't locked in
  const MIN_EXPERIENCE_SECONDS = 15;
  useEffect(() => {
    let interval;
    const step = tourSteps[currentStep];
    if (step?.id === 'try-it' && showDotMovement && !canContinue) {
      interval = setInterval(() => {
        setExperienceTimer(prev => {
          if (prev >= MIN_EXPERIENCE_SECONDS) {
            setCanContinue(true);
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentStep, showDotMovement, canContinue, tourSteps]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      if (tourSteps[nextStep].action) {
        tourSteps[nextStep].action();
      }
    }
  };

  const handleSkip = () => {
    markTourCompleted();
    navigate(returnPath);
  };

  const handleStartTrial = () => {
    markTourCompleted();
    navigate("/subscribe");
  };

  const currentStepData = tourSteps[currentStep];

  return (
    <SessionThemeWrapper className="flex flex-col min-h-screen bg-session-bg text-session-text">
      <DynamicTitle>Interactive Demo</DynamicTitle>

      {/* Main Session Area */}
      <div className="flex-grow relative">
        {/* Hamburger + hint: direct child with z-50 so it sits above the top bar (z-40) */}
        {!sidebarVisible && showDotMovement && (
          <div className="absolute top-4 left-4 z-50 flex items-center gap-2 pointer-events-none">
            <div className="pointer-events-auto">
              <button
                onClick={() => setSidebarVisible(true)}
                className="text-xl focus:outline-none p-2 bg-black bg-opacity-50 rounded hover:bg-opacity-70 transition-colors"
                aria-label="Open settings"
              >
                <span className="hamburger text-session-accent">&#9776;</span>
              </button>
            </div>
            <span className="text-white text-sm font-medium whitespace-nowrap animate-pulse drop-shadow-md">
              ← Settings
            </span>
          </div>
        )}
        {/* Background with current settings */}
        <div 
          className="w-full h-full flex items-center justify-center relative"
          style={{ backgroundColor: bgColor }}
        >

          {/* Sidebar */}
          {sidebarVisible && (
            <div className="absolute left-0 top-0 h-full z-50">
              <Sidebar
                isVisible={sidebarVisible}
                closeSidebar={() => setSidebarVisible(false)}
                dotSize={dotSize}
                setDotSize={setDotSize}
                dotSpeed={dotSpeed}
                setDotSpeed={setDotSpeed}
                setExitSession={handleSkip}
                hideExitButton
              />
            </div>
          )}

          {/* Dot Movement */}
          {showDotMovement && (
            <div className="w-full h-full flex items-center justify-center relative">
              <DotMovement
                shouldRun={showDotMovement}
                isPaused={isPaused}
                bgColor={bgColor}
                dotColor={dotColor}
                dotGlow={dotGlow}
                dotBrightness={dotBrightness}
                dotSize={dotSize}
                dotSpeed={dotSpeed}
                onCompletion={() => {}}
                soundFile={soundFile}
                mute={mute}
                volume={volume}
              />
            </div>
          )}
        </div>

        {/* Experience Step - Floating UI (no blocking overlay) */}
        {currentStepData.isExperienceStep && (
          <>
            {/* Top instruction banner - soft label */}
            <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 text-white p-4 z-40">
              <div className="max-w-2xl mx-auto text-center">
                <p className="text-lg font-medium mb-1">
                  Take your time — follow the dot with your eyes
                </p>
                <p className="text-sm text-gray-300">
                  Use the settings on the left to adjust colors, size, and speed to your comfort
                </p>
              </div>
            </div>

            {/* Bottom controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 p-4 z-40">
              <div className="max-w-2xl mx-auto">
                {/* Timer / Progress */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="flex items-center gap-2 text-white">
                    <span className="text-sm">
                      {canContinue
                        ? "You can continue whenever you're ready"
                        : `Follow the dot for ${Math.max(0, MIN_EXPERIENCE_SECONDS - experienceTimer)} more seconds...`
                      }
                    </span>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex justify-center items-center gap-4">
                  <button
                    onClick={() => setIsPaused(!isPaused)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors"
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                  
                  <button
                    onClick={handleNext}
                    disabled={!canContinue}
                    className={`px-6 py-2 rounded-md font-semibold transition-colors ${
                      canContinue 
                        ? 'bg-accent text-white hover:bg-accentDark' 
                        : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    {canContinue ? "I'm Ready to Continue" : "Keep Following..."}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Standard Tour Overlay */}
        {currentStepData.showOverlay && (
          <div className="absolute inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg border border-tint mx-4 p-6 relative max-w-md">
              {/* Progress Indicator */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-textSecondary mb-2">
                  <span>Step {currentStep + 1} of {tourSteps.length}</span>
                  <span>{Math.round(((currentStep + 1) / tourSteps.length) * 100)}%</span>
                </div>
                <div className="w-full bg-[rgba(132,59,98,0.08)] rounded-full h-2">
                  <div 
                    className="bg-accent h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-text mb-4">
                {currentStepData.title}
              </h2>
              <p className="text-textSecondary mb-4 leading-relaxed">
                {currentStepData.content}
              </p>
              {/* Session structure step: check in → follow dot → reflect → check out */}
              {currentStepData.showStructureList && (
                <ol className="list-decimal list-inside text-textSecondary space-y-2 mb-6 pl-2">
                  <li>Check in — set your intention and mood</li>
                  <li>Follow the dot — bilateral stimulation</li>
                  <li>Reflect — notice what you feel</li>
                  <li>Check out — close the session</li>
                </ol>
              )}

              {/* Reflection step tip */}
              {currentStepData.id === 'reflection' && (
                <div className="bg-background border-l-4 border-accent p-4 mb-6 rounded">
                  <p className="text-text text-sm font-medium">
                    💡 <strong>Good to know:</strong> Effects often become more noticeable with 
                    longer sessions and regular practice.
                  </p>
                </div>
              )}

              {/* Complete step - interstitial: Start Free Trial + Learn more first (USER_FEEDBACK) */}
              {currentStepData.isCompleteStep && (
                <div className="space-y-3 mt-6">
                  <p className="text-textSecondary text-sm mb-2">
                    You just experienced bilateral stimulation.
                  </p>
                  <button
                    onClick={handleStartTrial}
                    className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Start Free Trial
                  </button>
                  <a
                    href="/subscribe"
                    onClick={(e) => {
                      e.preventDefault();
                      markTourCompleted();
                      navigate("/subscribe");
                    }}
                    className="block w-full text-center text-accent hover:text-primary font-medium py-2"
                  >
                    See pricing
                  </a>
                </div>
              )}

              {/* Standard Action Buttons (not on complete step) */}
              {!currentStepData.isCompleteStep && (
                <div className="flex justify-between">
                  <button
                    onClick={handleSkip}
                    className="text-textSecondary hover:text-text transition-colors"
                  >
                    Exit Demo
                  </button>
                  <div className="flex gap-3">
                    {currentStep > 0 && !currentStepData.isExperienceStep && (
                      <button
                        onClick={() => {
                          const prevStep = currentStep - 1;
                          setCurrentStep(prevStep);
                          if (tourSteps[prevStep].action) {
                            tourSteps[prevStep].action();
                          }
                        }}
                        className="px-4 py-2 border border-tint rounded-md text-text hover:bg-surfaceTint transition-colors"
                      >
                        Back
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      className="px-6 py-2 rounded-md transition-colors font-semibold bg-accent text-white hover:bg-accentDark"
                    >
                      {(currentStepData.id === 'what-to-do' || currentStepData.id === 'session-structure') ? "Let's Try It" : 'Next'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SessionThemeWrapper>
  );
}

export default PublicDemo;
